import { useMemo, useState } from "react";
import styles from "./DealerAnalytics.module.scss";
import { Card, ReportFrame, Segmented, SortTable, StatTile, fmtNum, timeAgo, useReport } from "./shared";

const FILTERS = [
  { value: "warm", label: "Warm prospects" },
  { value: "open", label: "Open lead" },
  { value: "buyers", label: "Bought" },
  { value: "all", label: "Everyone" },
];

const isWarm = (c) => !c.purchases && c.saves + c.leads + c.inspections >= 2;

const Customers = ({ range, refreshKey, onOpenCustomer }) => {
  const state = useReport("customers", range, refreshKey);
  const [filter, setFilter] = useState("warm");
  const [query, setQuery] = useState("");

  return (
    <ReportFrame state={state}>
      {(d) => (
        <CustomersBody d={d} filter={filter} setFilter={setFilter} query={query} setQuery={setQuery} onOpenCustomer={onOpenCustomer} />
      )}
    </ReportFrame>
  );
};

const CustomersBody = ({ d, filter, setFilter, query, setQuery, onOpenCustomer }) => {
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return d.customers.filter((c) => {
      if (filter === "warm" && !isWarm(c)) return false;
      if (filter === "open" && !c.openLead) return false;
      if (filter === "buyers" && !c.purchases) return false;
      if (!q) return true;
      return [c.name, c.email, ...c.cars].some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [d.customers, filter, query]);

  const open = (c) => {
    if (c.userId) onOpenCustomer({ userId: c.userId });
    else if (c.latestInquiryId) onOpenCustomer({ inquiryId: c.latestInquiryId });
  };

  return (
    <div className={styles.stack}>
      <div className={styles.tiles}>
        <StatTile label="People engaged" value={fmtNum(d.totals.people)} sub="saved a car, sent a lead or asked for an inspection" />
        <StatTile label="Warm prospects" value={fmtNum(d.totals.warm)} sub="two or more of those, nothing bought yet" />
        <StatTile label="Buyers" value={fmtNum(d.totals.buyers)} sub="among them, with a recorded purchase" />
      </div>

      <Card
        title="Who to follow up with"
        sub="Ranked by interest this period (save 5, lead 20, inspection 30, signed-in car view 1). Cars are the ones they engaged with most. Opening a customer takes you to their page under Dealer Stock."
        actions={(
          <div className={styles.filterRow}>
            <Segmented label="Show" options={FILTERS} value={filter} onChange={setFilter} />
            <input
              type="search"
              className={styles.actionBtn}
              style={{ cursor: "text", minWidth: 180 }}
              placeholder="Name, email or car"
              aria-label="Filter customers"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        )}
      >
        <SortTable
          rowKey={(r) => r.key}
          rows={rows}
          limit={100}
          empty="Nobody matches."
          columns={[
            {
              key: "name",
              label: "Customer",
              render: (r) => (
                <div>
                  {(r.userId || r.latestInquiryId)
                    ? <button type="button" className={styles.link} onClick={() => open(r)}>{r.name}</button>
                    : r.name}
                  {r.guest && <span className={styles.chip} style={{ marginLeft: 6 }}>Guest</span>}
                  <div className={`${styles.small} ${styles.muted}`}>{r.email}</div>
                </div>
              ),
            },
            { key: "saves", label: "Saves", num: true },
            { key: "leads", label: "Leads", num: true },
            { key: "inspections", label: "Insp.", num: true },
            { key: "views", label: "Views", num: true },
            { key: "purchases", label: "Bought", num: true },
            { key: "cars", label: "Interested in", sortable: false, render: (r) => r.cars.join(" · ") || "–" },
            { key: "channel", label: "Came from", render: (r) => r.channel || <span className={styles.muted}>–</span> },
            { key: "lastActivity", label: "Last active", num: true, sort: (r) => new Date(r.lastActivity || 0).getTime(), render: (r) => timeAgo(r.lastActivity) },
            { key: "score", label: "Interest", num: true, bar: true },
          ]}
        />
      </Card>
    </div>
  );
};

export default Customers;
